import { useState, useCallback, useRef } from 'react'
import { Upload, FileSpreadsheet, FileText, X, Loader2, Check, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { apiClient } from '@/lib/api-client'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

interface UploadResult {
  created: number
  skipped: number
  errors: string[]
  warnings: string[]
  classifications: Array<{
    name: string
    displayName: string
    level: string
    action: 'created' | 'skipped'
    reason?: string
  }>
}

interface ClassificationUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ACCEPTED_EXTENSIONS = '.xlsx,.csv,.md'
const MAX_FILE_SIZE = 10 * 1024 * 1024

function getFileIcon(filename: string) {
  const ext = filename.toLowerCase().slice(filename.lastIndexOf('.'))
  if (ext === '.xlsx') return FileSpreadsheet
  return FileText
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function ClassificationUploadDialog({
  open,
  onOpenChange,
}: ClassificationUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [result, setResult] = useState<UploadResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const resetState = useCallback(() => {
    setFile(null)
    setIsDragging(false)
    setIsUploading(false)
    setResult(null)
  }, [])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        resetState()
      }
      onOpenChange(nextOpen)
    },
    [onOpenChange, resetState],
  )

  const validateFile = useCallback((f: File): string | null => {
    const ext = f.name.toLowerCase().slice(f.name.lastIndexOf('.'))
    if (!['.xlsx', '.csv', '.md'].includes(ext)) {
      return `Unsupported file type: ${ext}. Accepted: .xlsx, .csv, .md`
    }
    if (f.size > MAX_FILE_SIZE) {
      return `File too large: ${formatFileSize(f.size)}. Maximum: 10 MB`
    }
    return null
  }, [])

  const handleFileSelect = useCallback(
    (f: File) => {
      const error = validateFile(f)
      if (error) {
        toast.error(error)
        return
      }
      setFile(f)
      setResult(null)
    },
    [validateFile],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      const droppedFile = e.dataTransfer.files[0]
      if (droppedFile) {
        handleFileSelect(droppedFile)
      }
    },
    [handleFileSelect],
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0]
      if (selectedFile) {
        handleFileSelect(selectedFile)
      }
    },
    [handleFileSelect],
  )

  const handleUpload = useCallback(async () => {
    if (!file) return

    setIsUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await apiClient.post('/classifications/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      const uploadResult = response.data.data as UploadResult
      setResult(uploadResult)

      if (uploadResult.created > 0) {
        queryClient.invalidateQueries({ queryKey: ['classifications'] })
        toast.success(`Created ${uploadResult.created} classification(s)`)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed'
      toast.error(message)
    } finally {
      setIsUploading(false)
    }
  }, [file, queryClient])

  const FileIcon = file ? getFileIcon(file.name) : Upload

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Classification Rules</DialogTitle>
          <DialogDescription>
            Import classification rules from Excel (.xlsx), CSV, or Markdown (.md) files.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Drop zone */}
          {!result && (
            <div
              className={cn(
                'border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer',
                isDragging && 'border-primary bg-primary/5',
                !isDragging && !file && 'border-muted-foreground/25 hover:border-primary/50',
                file && 'border-primary/50 bg-primary/5',
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_EXTENSIONS}
                onChange={handleInputChange}
                className="hidden"
              />

              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileIcon className="h-8 w-8 text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => {
                      e.stopPropagation()
                      setFile(null)
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="font-medium">Drop a file here or click to browse</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Supports .xlsx, .csv, and .md files (max 10 MB)
                  </p>
                </>
              )}
            </div>
          )}

          {/* Format help */}
          {!file && !result && (
            <div className="text-sm text-muted-foreground space-y-2">
              <p className="font-medium">Expected columns:</p>
              <div className="flex flex-wrap gap-1.5">
                {['Name*', 'Level*', 'Description', 'Regulatory Frameworks', 'Name Patterns', 'Data Patterns'].map(
                  (col) => (
                    <Badge key={col} variant="secondary" className="text-xs">
                      {col}
                    </Badge>
                  ),
                )}
              </div>
              <p className="text-xs">* Required. Patterns are comma-separated regex strings.</p>
            </div>
          )}

          {/* Upload result */}
          {result && (
            <div className="space-y-3">
              {/* Summary */}
              <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-1.5 text-sm">
                  <Check className="h-4 w-4 text-green-600" />
                  <span>{result.created} created</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <span>{result.skipped} skipped</span>
                </div>
                {result.errors.length > 0 && (
                  <div className="flex items-center gap-1.5 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4" />
                    <span>{result.errors.length} error(s)</span>
                  </div>
                )}
              </div>

              {/* Errors */}
              {result.errors.length > 0 && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-3">
                  <p className="text-sm font-medium text-destructive mb-1">Errors</p>
                  <ul className="text-xs text-destructive space-y-0.5">
                    {result.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Classifications table */}
              {result.classifications.length > 0 && (
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Level</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.classifications.map((c, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{c.displayName}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {c.level}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {c.action === 'created' ? (
                              <Badge className="text-xs bg-green-100 text-green-800 hover:bg-green-100">
                                Created
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">
                                Skipped{c.reason ? ` — ${c.reason}` : ''}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {result ? (
            <Button onClick={() => handleOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpload} disabled={!file || isUploading}>
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-1" />
                    Upload
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
